import { translationKey } from '../i18n';
import { localize } from '../i18n';
import { sheetSpacing } from '../design/tokens';
import SingleLineText from './ui/InternationalText';
import Button from './ui/FitButton';
import LoadingLabel from './LoadingLabel';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BottomSheet from './BottomSheet';

import { FONT_FAMILY as FONT_STACK } from '../design/tokens';
const MAX_LENGTH = 80;

const TEMPLATES = [
    { id: 'time_to_take', label: translationKey('지금 챙겨드세요'), message: translationKey('약 드실 시간이에요. 지금 챙겨주세요.') },
    { id: 'not_confirmed_yet', label: translationKey('아직 안 드신 것 같아요'), message: translationKey('아직 약을 안 드신 것 같아요. 잊지 말고 챙겨주세요.') },
    { id: 'worried_reminder', label: translationKey('한 번 더 알려드려요'), message: translationKey('걱정돼서 알려드려요. 약 꼭 챙겨드세요.') },
    { id: 'running_late', label: translationKey('조금 늦었어요'), message: translationKey('조금 늦었어요. 지금 챙겨드시면 돼요.') },
    { id: 'tap_if_done', label: translationKey('완료했다면 눌러주세요'), message: translationKey('챙기셨다면 앱에서 완료 버튼을 눌러주세요.') },
];

export default function GuardianNudgeSheet({
    open,
    target,
    logs = [],
    onClose,
    onSubmit,
    onRequestReward,
    submitting = false,
    rewardSubmitting = false,
    limitReached = false,
    rewardAvailable = true,
}) {
    const [mode, setMode] = useState('TEMPLATE');
    const [templateId, setTemplateId] = useState(TEMPLATES[0].id);
    const [customMessage, setCustomMessage] = useState('');
    const [scrollToBottomKey, setScrollToBottomKey] = useState(0);

    useLayoutEffect(() => {
        if (!open) return;
        setMode('TEMPLATE');
        setTemplateId(TEMPLATES[0].id);
        setCustomMessage('');
        setScrollToBottomKey(0);
    }, [open]);

    useEffect(() => {
        if (!open || !limitReached) return;
        setScrollToBottomKey((key) => key + 1);
    }, [open, limitReached]);

    const actionableCount = logs.filter((log) => log.status === 'PENDING' || log.status === 'MISSED').length;
    const customTrimmed = customMessage.replace(/\s+/g, ' ').trim();
    const canSubmit = !submitting
        && logs.length > 0
        && (mode === 'TEMPLATE' || (customTrimmed.length > 0 && customTrimmed.length <= MAX_LENGTH));

    const handleSubmit = () => {
        if (!canSubmit) return;
        const payload = mode === 'TEMPLATE'
            ? { messageType: 'TEMPLATE', templateId }
            : { messageType: 'CUSTOM', message: customTrimmed };
        onSubmit(payload);
    };

    return (
        <BottomSheet
            open={open}
            onClose={onClose}
            borderRadius="22px 22px 0 0"
            handleColor="#E2DDD2"
            px="20px"
            scrollToBottomKey={scrollToBottomKey}
        >
            <Box sx={{ fontFamily: FONT_STACK }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
                    <Box sx={{ width: 42, height: 42, flexShrink: 0, borderRadius: '50%', bgcolor: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CampaignOutlinedIcon sx={{ color: '#8a7000', fontSize: 21 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <SingleLineText sx={{ fontSize: '1.12rem', fontWeight: 800, color: '#1F1D1B', lineHeight: 1.35 }}>
                            {target?.patientNickname || localize("가족")}{localize("에게 챙겨달라고 알리기")}</SingleLineText>
                    </Box>
                </Box>

                <Box sx={{
                    bgcolor: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: '14px',
                    px: '14px',
                    py: '15px',
                    mb: '14px',
                }}>
                    <Typography sx={{ fontSize: '0.98rem', fontWeight: 800, color: '#5a4a00', lineHeight: 1.4 }}>
                        {actionableCount > 0
                            ? localize("복용 대기중인 {v0}개 약이 있어요.", { v0: actionableCount })
                            : localize("오늘 약 {v0}개 기준으로 알림을 보낼게요.", { v0: logs.length })}
                    </Typography>
                </Box>

                <Box sx={{ bgcolor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '14px', p: '14px', mb: sheetSpacing.action }}>
                    <SingleLineText sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#666', mb: 1 }}>{localize("문구 선택")}</SingleLineText>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                        {TEMPLATES.map((template) => {
                            const selected = mode === 'TEMPLATE' && template.id === templateId;
                            return (
                                <Box
                                    key={template.id}
                                    component="button"
                                    type="button"
                                    onClick={() => { setMode('TEMPLATE'); setTemplateId(template.id); }}
                                    sx={{
                                        textAlign: 'left', p: '12px', borderRadius: '12px', cursor: 'pointer',
                                        border: selected ? '1px solid #E8D040' : '1px solid #E5E7EB',
                                        bgcolor: selected ? '#FFF8CC' : '#fff',
                                    }}
                                >
                                    <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: '#1F1D1B', mb: 0.4, lineHeight: 1.35, minWidth: 0, overflowWrap: 'anywhere' }}>
                                        {localize(template.label)}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.84rem', color: '#6B7280', lineHeight: 1.45 }}>
                                        {localize(template.message)}
                                    </Typography>
                                </Box>
                            );
                        })}
                        <Box
                            component="button"
                            type="button"
                            onClick={() => {
                                setMode('CUSTOM');
                                setScrollToBottomKey((key) => key + 1);
                            }}
                            sx={{
                                textAlign: 'left', p: '12px', borderRadius: '12px', cursor: 'pointer',
                                border: mode === 'CUSTOM' ? '1px solid #D4ECDA' : '1px solid #E5E7EB',
                                bgcolor: mode === 'CUSTOM' ? '#F1FBF3' : '#fff',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.4 }}>
                                <EditOutlinedIcon sx={{ fontSize: 17, color: mode === 'CUSTOM' ? '#1F8A3D' : '#6B7280' }} />
                                <SingleLineText sx={{ fontSize: '0.92rem', fontWeight: 800, color: '#1F1D1B' }}>{localize("직접 입력")}</SingleLineText>
                            </Box>
                            <Typography sx={{ fontSize: '0.84rem', color: '#6B7280', lineHeight: 1.45, minWidth: 0, overflowWrap: 'anywhere' }}>{localize("원하는 문구를 적어요")}</Typography>
                        </Box>
                    </Box>
                </Box>

                {mode === 'CUSTOM' && (
                    <Box sx={{ bgcolor: '#F1FBF3', border: '1px solid #D4ECDA', borderRadius: '14px', p: '14px', mb: sheetSpacing.action }}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={5}
                            placeholder={localize("직접 메시지를 입력할 수 있어요")}
                            value={customMessage}
                            onChange={(event) => {
                                setCustomMessage(event.target.value.slice(0, MAX_LENGTH));
                            }}
                            inputProps={{ maxLength: MAX_LENGTH, 'data-keyboard-lift-action': 'true' }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: '#fff',
                                    borderRadius: '12px',
                                    fontFamily: FONT_STACK,
                                },
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.8 }}>
                            <SingleLineText sx={{ fontSize: '0.82rem', color: '#6B7280' }}>{localize("링크는 보낼 수 없어요.")}</SingleLineText>
                            <Typography sx={{ fontSize: '0.82rem', color: '#6B7280' }}>
                                {customMessage.length}/{MAX_LENGTH}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {limitReached ? (
                    <Box sx={{ px: 0.1, mt: 0.2 }}>
                        <Typography sx={{ fontSize: '0.9rem', color: '#888', fontWeight: 650, lineHeight: 1.55, wordBreak: 'keep-all', minWidth: 0, overflowWrap: 'anywhere' }}>
                            {rewardAvailable ? localize('오늘 무료 알림을 다 썼어요.') : localize('오늘 챙김 요청 30회를 모두 사용했어요. 내일 다시 이용해주세요.')}
                        </Typography>
                        {rewardAvailable && <Button
                            variant="text"
                            role="button"
                            tabIndex={0}
                            onClick={onRequestReward}
                            disabled={rewardSubmitting}
                            startIcon={<PlayCircleIcon sx={{ fontSize: 17 }} />}
                            endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
                            sx={{
                                mt: 1.25,
                                ml: -0.3,
                                px: 0.7,
                                py: 0.55,
                                minWidth: 0,
                                justifyContent: 'flex-start',
                                color: '#8a7000',
                                fontSize: '0.86rem',
                                fontWeight: 800,
                                borderRadius: 2.2,
                                bgcolor: '#fffbeb',
                                '& .MuiButton-startIcon': { mr: 0.45 },
                                '& .MuiButton-endIcon': { ml: 0.2 },
                                '&:hover': { bgcolor: '#fff6cc', color: '#6f5a00' },
                                '&.Mui-disabled': { bgcolor: '#f3f4f6', color: '#9ca3af' },
                            }}
                        >
                            {rewardSubmitting ? <LoadingLabel>{localize("광고 준비 중...")}</LoadingLabel> : localize('광고 보고 3회 더 받기')}
                        </Button>}
                    </Box>
                ) : (
                    <Button
                        fullWidth
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        sx={{
                            borderRadius: '14px', py: 1.5, bgcolor: '#ffe566', color: '#5a4a00',
                            fontWeight: 800, fontSize: '1rem', boxShadow: 'none',
                            '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                            '&.Mui-disabled': { bgcolor: '#f3f4f6', color: '#9ca3af' },
                        }}
                    >
                        {submitting ? <LoadingLabel>{localize("알림 보내는 중...")}</LoadingLabel> : localize("챙겨달라고 알리기")}
                    </Button>
                )}
            </Box>
        </BottomSheet>
    );
}
